import { useCallback, useLayoutEffect, useState } from 'react';

/**
 * Observes an element's rendered (border-box) width and reports whether it is
 * at least `thresholdPx` wide. Re-renders only when that boolean flips.
 * Returns false until the first measurement.
 */
export const useElementWidthAtLeast = (
  thresholdPx: number
): {
  ref: (node: HTMLElement | null) => void;
  isAtLeast: boolean;
} => {
  const [isAtLeast, setIsAtLeast] = useState(false);
  const [element, setElement] = useState<HTMLElement | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  useLayoutEffect(() => {
    if (!element) {
      setIsAtLeast(false);

      return;
    }

    const evaluate = (): void => {
      setIsAtLeast(element.getBoundingClientRect().width >= thresholdPx);
    };

    evaluate();

    if (typeof ResizeObserver !== 'function') return;

    const resizeObserver = new ResizeObserver(evaluate);

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [element, thresholdPx]);

  return { ref, isAtLeast };
};
